import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { isMobile } from "react-device-detect";
import { Button, Dimmer, Header, Icon, Loader, Modal, Transition } from "semantic-ui-react";
import "../PostCard/PostCard.scss";
import "./EmailSharerResolver.scss";
import { shortenUrl, trackPostShare, getMediaBaseUrl } from "../../actions";
import Favicon from "react-favicon";
import { setShare } from "../utils/Amplitude";
import { 
  copyText, 
  downloadFile, 
  isTwitterMediaLink, 
  I18n,
  i18nKeys 
} from "../utils/functions";
import axiosRetry from "axios-retry";
import { useUpdateEffect } from "react-use";
import dummyImage from "../../images/dummy.jpg";
import { 
  facebookShareStrategy, 
  twitterShareStrategy,
  instagramShareStrategy,
  linkedinShareStrategy,
  whatsappShareStrategy,
  allAppsShareStrategy,
  smsShareStrategy
} from "../utils/sharing";
import {
  shareLink,
  shareNativeWithFile,
  shareNativeWithoutFile,
  pickShareBehavior,
  instagramIcon,
} from "../utils/sharing";

axiosRetry(axios, { retries: 3, retryCondition: (_error) => true });

const EmailSharerResolver = (props) => {
  const [imageURL, setImageURL] = useState(null);
  const [open, setOpen] = useState(false);
  const state = useSelector((state) => state.sharer);
  const dispatch = useDispatch();
  const campaignID = props.match.params.campaignId;
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const imageID = urlParams.get("image_id");
  const text = urlParams.get("text");
  const socialNetwork = urlParams.get("social_network");
  const url = urlParams.get("url");
  const linkCaption = urlParams.get("link_caption");
  const mediaUrl = urlParams.get("media_url");
  const shortenedUrl = urlParams.get("short_url");
  const [mediaType, setMediaType] = useState(undefined);
  const [mediaModal, setMediaModal] = useState(false);
  const [mediaModalMobile, setMediaModalMobile] = useState(false);
  const [doJiggle, setDoJiggle] = useState(false);

  const shareStrategy = socialNetwork === "twitter"? twitterShareStrategy :
                        socialNetwork === "facebook"? facebookShareStrategy :
                        socialNetwork === "instagram"? instagramShareStrategy :
                        socialNetwork === "linkedin"? linkedinShareStrategy :
                        socialNetwork === "whatsapp"? whatsappShareStrategy :
                        socialNetwork === "native"? allAppsShareStrategy :
                        socialNetwork === "sms" ? smsShareStrategy : null;

  const [shareBehavior, setShareBehavior] = useState(null);

  const currentDomain = window.location.origin;
  const [mobileShare, setMobileShare] = useState(null);
  const [shortenedLink, setShortenedLink] = useState(null);
  const [fullText, setFullText] = useState(text);
  const userAgent = navigator.userAgent;
  const [os, setOS] = useState();
  const [loading, setLoading] = useState(true);

  const i18n = new I18n();

  useEffect(() => {
    if(shortenedUrl) {
      setShortenedLink(shortenedUrl);
    }
  }, [shortenedUrl]);

  useEffect(() => {
    setDoJiggle((prev) => !prev);
  }, [open]);

  useEffect(() => {
    if(mediaUrl) {
      if(mediaUrl.slice(-4) === ".mp4") {
        setMediaType("video");
      }
      else if(mediaUrl.slice(-4) === ".gif") {
        setMediaType("gif");
      }
      else {
        setMediaType("image")
      }
      setImageURL(mediaUrl);
    }
  }, [mediaUrl]);

  useEffect(() => {
    async function getImageById() {
      const requestUrl = (state.apiMode === "test") ? 
                          `https://app-staging.speechifai.tech/api/image/resolve_id/${imageID}` : 
                          `${currentDomain}/api/image/resolve_id/${imageID}`;

      axios.get(requestUrl)
      .then((res) => {
        console.log(res);

        const filename = res.data.filename;
        const fullURL = res.data.fullURL;
        const filenameParts = filename.split('.');
        const mediaPath = fullURL.substr(0, fullURL.lastIndexOf("/") + 1);

        if(filenameParts.length > 1) {
          const extension = filenameParts[filenameParts.length - 1];

          if((extension === "gif") && (fullURL.includes("giphy"))) {
            // giphy
            setMediaType("giphy");
            setImageURL(fullURL);
          }
          else if(extension === "mp4") {
            // video
            setMediaType("video");
            setImageURL(mediaPath + filename);
          }
          else if(extension === "gif") {
            // gif
            setMediaType("gif");
            setImageURL(mediaPath + filename);
          }
          else {
            // image
            setMediaType("image");
            setImageURL(fullURL);
          }
        }
        else {
          // youtube
          setMediaType("youtube");
          setImageURL("https://www.youtube.com/watch?v=" + filename);
        }
      })
      .catch((err) => {
        console.log(err.message, "MESSAGE");
        console.log(err.stauts, "STATUS");
      });
    }
    if (imageID && !imageURL & !mediaUrl) {
      getImageById(imageID);
    }
  }, [imageID, state.apiMode, currentDomain]);

  useUpdateEffect(() => {
    if (mobileShare === null) {
      checkNavigator();
      return;
    }

    if(url && !shortenedLink) {
      // waiting for link to be shortened
      return;
    }

    if(!imageURL || !mediaType) {
      // waiting for image to be resolved
      return;
    }

    setShareBehavior(pickShareBehavior(
      shareStrategy.behavior,
      isMobile && mobileShare,
      mediaType,
      os
    ));
    
  }, [shareStrategy, imageURL, shortenedLink, mobileShare, mediaType]);

  useUpdateEffect(async () => {
    if(shareBehavior) {
      setLoading(false);

      var ret = 1;
      if (
        shareBehavior === "nativeWithFile" ||
        shareBehavior === "nativeWithoutFile"
      ) {
        setMediaModalMobile(true);
        return;
      } else if (shareBehavior === "shareLink") {
        // check if text needs to be copied to clipboard
        if (!shareStrategy.config.textParam) {
          setOpen(true);
          return;
        } else {
          ret = await shareLink(
            fullText,
            shortenedLink,
            shareStrategy.config, true
          );
        }
      } else if (shareBehavior === "downloadMedia") {
        setMediaModal(true);
        downloadMedia();
        return;
      }

      if (ret === 0) {
        trackShare();
      }
    }
  }, [shareBehavior]);

  const sharePost = async () => {
    const linkToShare = shortenedLink;    
    if (!shareStrategy.config.textParam) {
      if(shareStrategy.config.urlParam) {
        copyText(`${fullText}`);
      }
      else {
        copyText(`${fullText} ${linkToShare}`);
      }
    }

    shareLink(fullText, linkToShare, shareStrategy.config, true).then((ret) => {
      if (ret === 0) {
        trackShare();
      }
    });
  };

  useEffect(() => {
    if (
      campaignID &&
      url &&
      imageID &&
      socialNetwork &&
      state.apiMode &&
      (!shortenedLink)
    ) {
      getLinkToShare().then((res) => {
        setShortenedLink(res);
      });      
    }
  }, [campaignID, url, imageID, socialNetwork, state.apiMode]);  

  useEffect(() => {
    // SET OS
    if (/windows phone/i.test(userAgent)) {
      setOS("Windows Phone");
    } else if (/android/i.test(userAgent)) {
      setOS("Android");
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setOS("IOS");
    } else {
      setOS("unknown");
    }
  }, []);

  const checkNavigator = async () => {
    if (typeof navigator.canShare === "function") {
      const fileObject = new File([dummyImage], 'dummy.jpg', { type: "image/jpeg" });
      const filesArray = [fileObject];
      const shareData = {
        files: filesArray,
      };
      if (navigator.canShare(shareData)) {
        console.log(navigator.canShare(shareData), "CAN SHARE");
        setMobileShare(true);
      } else {
        console.log(navigator.canShare(shareData), "CANT SHARE");
        setMobileShare(false);
      }
    } else {
      console.log("Navigator can share is not a function");
      setMobileShare(false);
    }
  };

  async function getLinkToShare() {
    if (mediaType === "video" || mediaType === "gif") {
      return "";
    } else if (mediaType === "youtube") {
      return imageURL;
    } else if (mediaType === "giphy") {
      return shareBehavior === "shareLink" ? imageURL : "";
    } else if (url && url.substring(0, 2) === "@@") {
      return url.slice(2);
    } else if (url && isTwitterMediaLink(url)) {
      return url;
    } else if (shortenedLink) {
      return shortenedLink;
    } else if (url) {
      return await dispatch(
        shortenUrl(
          {
            campaignID: campaignID,
            url: url,
            imageID: imageID,
            socialNetwork: socialNetwork,
            linkCaption: linkCaption ? linkCaption : "Learn more",
          },
          state.apiMode
        )
      ).then((res) => {
        return res;
      });
    }
    return "";
  }

  const downloadMedia = () => {
    downloadFile(imageURL, mediaType);
  };

  const nativeShare = async () => {
    const linkToShare = shortenedLink;
    var ret = 1;
    if (shareBehavior === "nativeWithFile") {
      if (linkToShare) {
        copyText(`${fullText} ${linkToShare}`);
      } else {
        copyText(`${fullText}`);
      }

      const textToShare = shareStrategy.config.nativeSkipText?.includes(os)
        ? undefined
        : linkToShare
        ? `${fullText} ${linkToShare}`
        : fullText;

      ret = await shareNativeWithFile(imageURL, mediaType, textToShare);
    } else {
      const textToShare = shareStrategy.config.nativeSkipText?.includes(os)
        ? undefined
        : fullText;
      ret = await shareNativeWithoutFile(textToShare, linkToShare);
    }

    if (ret === 0) {
      trackShare();
    }
  };

  const trackShare = () => {
    const platformName = shareStrategy.config.platformName === "X / Twitter" ? "Twitter" : shareStrategy.config.platformName;
    dispatch(
      trackPostShare(
        {
          campaignID: campaignID,
          sharedText: fullText,
          shared_url: mediaType === "image" ? url : "",
          sharedPlatform: platformName,
          sharedImageURL: imageURL,
          postType: "Ready Made Post",
          embed: false,
          email: true,
        },
        state.apiMode
      )
    );

    if (state.apiMode === "prod") {
      setShare({
        Platform: platformName,
      });
    }
  };

  return (
    <>
      <Favicon url={"https://app.speechifai.tech/favicon.ico"} />
      {loading ? (
        <div className="home-loader">
          <Dimmer active inverted style={{ backgroundColor: "transparent" }}>
            <Loader inverted>Loading</Loader>
          </Dimmer>
        </div>
      ) : (
        <div>
          <Modal
          onOpen={() => setOpen(true)}
          open={open}
          className="generic-modal"
          style={{
            "--platform_color": shareStrategy.config.color,
          }}
        >
          <Modal.Header>
            {i18n.getText(i18nKeys.textCopied)}
          </Modal.Header>
          <Modal.Content>
            <div className="modal-check">
            <Transition
                animation={"tada"}
                duration={800}
                visible={doJiggle}
              >
                <Icon name="check" size="huge" />
              </Transition>
              <Modal.Description>
                <Header>
                  {i18n.getText(i18nKeys.pasteTextAfterContinuing)}
                </Header>
              </Modal.Description>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={sharePost}>
              {i18n.getText(i18nKeys.continueToPlatform,shareStrategy.config.platformName)}
            </Button>
          </Modal.Actions>
        </Modal>

      <Modal
          onOpen={() => setMediaModal(true)}
          open={mediaModal}
          className="generic-modal"
          style={{
            "--platform_color": shareStrategy.config.color,
          }}
        >
          <Modal.Header>
            {i18n.getText(i18nKeys.textCopied)}
          </Modal.Header>
          <Modal.Content>
            <div className="modal-check">
              <Modal.Description>
                <Header>
                  {i18n.getText(i18nKeys.followSteps)}
                  : <br />
                  1.{" "}
                  {i18n.getText(i18nKeys.clickButtonBelow, shareStrategy.config.platformName)}
                  <br />
                  {socialNetwork === "instagram" ? (
                    <>
                      2.{" "}{i18n.getText(i18nKeys.clickOnInstagramIcon,instagramIcon)}
                      <br />
                      3.{" "}
                      {i18n.getText(i18nKeys.selectDownloadedFile)}
                      <br />
                      4.{" "}
                      {i18n.getText(i18nKeys.pasteCaptionInTextbox)}
                    </>
                  ) : (
                    <>
                      2.{" "}
                      {i18n.getText(i18nKeys.selectDownloadedFile)}
                      <br />
                      {!shareStrategy.config.textParam && (
                        <>
                          3.{" "}
                          {i18n.getText(i18nKeys.pasteCaptionInTextbox)}
                        </>
                      )}
                    </>
                  )}
                </Header>
              </Modal.Description>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={sharePost}>
            {i18n.getText(i18nKeys.continueToPlatform,shareStrategy.config.platformName)}
            </Button>
          </Modal.Actions>
        </Modal>
        {/* Mobile Modal */}
        <Modal
          onOpen={() => setMediaModalMobile(true)}
          open={mediaModalMobile}
          className="generic-modal-mobile"
          style={{ "--platform_color": shareStrategy.config.color }}
        >
          <Modal.Header>
          {i18n.getText(i18nKeys.textCopied)}
          </Modal.Header>
          <Modal.Content>
            <div className="modal-check">
              <Modal.Description>
                <Header>
                  <>
                  {i18n.getText(i18nKeys.followSteps)}
                    : <br />
                    1.{" "}{i18n.getText(i18nKeys.openPhoneShareMenu)}
                    <br />                    
                    2.{" "}{socialNetwork !== "native" ? 
                      i18n.getText(i18nKeys.selectPlatformApp,shareStrategy.config.platformName)
                      : i18n.getText(i18nKeys.selectAppToShare)}
                      {" "}
                    <br />
                    3.{" "}
                      {i18n.getText(i18nKeys.pasteCaptionInTextbox)}
                  </>
                </Header>
              </Modal.Description>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={nativeShare}>
            {i18n.getText(i18nKeys.continueToPlatform,shareStrategy.config.platformName)}
            </Button>
          </Modal.Actions>
        </Modal>

        </div>
        
      )}
    </>
  );
};

export default EmailSharerResolver;
